import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { uploadFilesTx, deleteFileTx } from '../../ducks/files.duck';
import { updateUserById, updateCustomSlug, fetchCustomSlugByUserId } from '../../util/api';
import { isValidCustomUrl } from '../../util/validators';
import { USER_TYPE_CREATOR } from '../../util/types';

import * as log from '../../util/log';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const UPDATE_MEDIA_REQUEST = 'app/EditListingPage/UPDATE_MEDIA_REQUEST';
export const UPDATE_MEDIA_SUCCESS = 'app/EditListingPage/UPDATE_MEDIA_SUCCESS';
export const UPDATE_MEDIA_ERROR = 'app/EditListingPage/UPDATE_MEDIA_ERROR';

export const SET_CUSTOM_SLUG = 'app/EditListingPage/SET_CUSTOM_SLUG';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,

  uploadMediaInProgress: false,
  media: {},
  mediaError: null,

  customSlug: { value: '', valueForLink: '', isValid: true, reason: null },
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case UPDATE_MEDIA_REQUEST:
      return { ...state, uploadMediaInProgress: true };
    case UPDATE_MEDIA_SUCCESS:
      return { ...state, media: payload, uploadMediaInProgress: false };
    case UPDATE_MEDIA_ERROR:
      return { ...state, mediaError: payload };
    case SET_CUSTOM_SLUG:
      return {
        ...state,
        customSlug: {
          ...state.customSlug,
          ...payload,
        },
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const uploadMediaRequest = () => ({
  type: UPDATE_MEDIA_REQUEST,
});

export const uploadMediaSuccess = payload => ({
  type: UPDATE_MEDIA_SUCCESS,
  payload,
});

export const uploadMediaError = error => ({
  type: UPDATE_MEDIA_ERROR,
  payload: error,
  error: true,
});

export const setCustomSlug = payload => ({
  type: SET_CUSTOM_SLUG,
  payload,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const postUpdateUserById = orderParams => (dispatch, getState, sdk) => {
  return updateUserById(orderParams)
    .then(response => {
      return response;
    })
    .catch(e => {
      log.error(e, 'update-user-data-failed', {
        userId: orderParams.userId,
      });
      throw e;
    });
};

const queryOwnListings = sdk => {
  return sdk.ownListings.query({});
};

export const updateProfile = (actionPayload, customSlug) => {
  return async (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(async response => {
        const { userType = '' } = response.data.data.attributes.profile.publicData;

        const profile = response.data.data.attributes.profile || {};
        const { firstName = '', lastName = '', displayName = '' } = profile;
        queryOwnListings(sdk)
          .then(listingsResp => {
            const listings = listingsResp?.data?.data || [];

            if (!listings?.length)return;
            
            const updatePromises = listings.map(({ id }) => {
              return sdk.ownListings.update({
                id,
                publicData: {
                  authorFirstName: firstName,
                  authorLastName: lastName,
                  authorDisplayName: displayName,
                },
              });
            });

            return Promise.allSettled(updatePromises).then(results => {
              results.forEach((res, idx) => {
                if (res.status === 'rejected') {
                  console.error(
                    `Error updating listing - ${listings[idx].id}:`,
                    res.reason
                  );
                }
              });
            });
          })
          .catch(err => {
            console.error('Error', err);
          });

        if (userType === USER_TYPE_CREATOR) {
          const { uuid } = response.data.data.id;
          const preparedSlug = customSlug.value.trim().toLowerCase();
          const result = await dispatch(
            postUpdateCustomSlug({ userId: uuid, customSlug: preparedSlug })
          );
          if (result) dispatch(setCustomSlug({ valueForLink: preparedSlug }));
        }

        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

export const requestMediaOnUpload = (file, id, userRole, entity, mediaName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(uploadMediaRequest());

  return dispatch(uploadFilesTx(file, id, userRole, entity, mediaName))
    .then(res => {
      const currentMedia = getState().ProfileSettingsPage.media;

      if (mediaName === 'athleteBanner') {
        dispatch(
          uploadMediaSuccess({
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: res.data,
          })
        );
      }
      if (mediaName === 'gameHighlights') {
        dispatch(
          uploadMediaSuccess({
            gameHighlights: res.data,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'gameHighlightsVideos') {
        dispatch(
          uploadMediaSuccess({
            gameHighlightsVideos: res.data,
            gameHighlights: currentMedia.gameHighlights,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'practiceClips') {
        dispatch(
          uploadMediaSuccess({
            practiceClips: res.data,
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'practiceClipsVideos') {
        dispatch(
          uploadMediaSuccess({
            practiceClipsVideos: res.data,
            practiceClips: currentMedia.practiceClips,
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      return res;
    })
    .catch(e => dispatch(uploadMediaError(storableError(e))));
};

export const requestMediaOnDelete = (key, id, entity, mediaName) => (dispatch, getState, sdk) => {
  return dispatch(deleteFileTx(key, id, entity, mediaName))
    .then(res => {
      const currentMedia = getState().ProfileSettingsPage.media;

      if (mediaName === 'athleteBanner') {
        dispatch(
          uploadMediaSuccess({
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: res.data,
          })
        );
      }
      if (mediaName === 'gameHighlights') {
        dispatch(
          uploadMediaSuccess({
            gameHighlights: res.data,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'gameHighlightsVideos') {
        dispatch(
          uploadMediaSuccess({
            gameHighlightsVideos: res.data,
            gameHighlights: currentMedia.gameHighlights,
            practiceClips: currentMedia.practiceClips,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'practiceClips') {
        dispatch(
          uploadMediaSuccess({
            practiceClips: res.data,
            practiceClipsVideos: currentMedia.practiceClipsVideos,
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      if (mediaName === 'practiceClipsVideos') {
        dispatch(
          uploadMediaSuccess({
            practiceClipsVideos: res.data,
            practiceClips: currentMedia.practiceClips,
            gameHighlights: currentMedia.gameHighlights,
            gameHighlightsVideos: currentMedia.gameHighlightsVideos,
            athleteBanner: currentMedia.athleteBanner,
          })
        );
      }
      return res;
    })
    .catch(e => dispatch(uploadMediaError(storableError(e))));
};

export const changeCustomSlug = value => async (dispatch, getState, sdk) => {
  const { uuid } = getState().user.currentUser.id;

  const result = await isValidCustomUrl(value, uuid);
  const { isValid, reason } = result;
  dispatch(setCustomSlug({ value, isValid, reason }));
};

export const postUpdateCustomSlug = payload => dispatch => {
  return updateCustomSlug(payload)
    .then(response => {
      return response;
    })
    .catch(e => {
      console.error(e, 'update-custom-slug-failed');
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return sdk.currentUser
    .show()
    .then(async response => {
      const currentUser = response?.data?.data;

      const athleteBanner = currentUser?.attributes?.profile?.publicData?.athleteBanner;
      const gameHighlights = currentUser?.attributes?.profile?.publicData?.gameHighlights;
      const gameHighlightsVideos =
        currentUser?.attributes?.profile?.publicData?.gameHighlightsVideos;
      const practiceClips = currentUser?.attributes?.profile?.publicData?.practiceClips;
      const practiceClipsVideos = currentUser?.attributes?.profile?.publicData?.practiceClipsVideos;
      const userType = currentUser?.attributes?.profile?.publicData?.userType;

      if (userType === USER_TYPE_CREATOR) {
        try {
          const { slug = '' } = await fetchCustomSlugByUserId(currentUser.id.uuid);
          dispatch(setCustomSlug({ value: slug, valueForLink: slug, isValid: true, reason: null }));
        } catch (e) {
          console.error(e);
        }
      }

      dispatch(
        uploadMediaSuccess({
          athleteBanner,
          gameHighlights,
          gameHighlightsVideos,
          practiceClips,
          practiceClipsVideos,
        })
      );

      return response;
    })
    .catch(e => {
      throw e;
    });
};
